// Sleeve manager - link/unlink sleeves, set contracts, toggle active.
// Shows intraday and overnight margin from AMP (via engine API).
// Click any row for full sleeve insight (/sleeves/:id).

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { accounts as accountsApi, algos as algosApi, portfolio } from "../api/endpoints";
import type { Account, AccountAlgoLink, Algo, SleevePerformance } from "../api/types";
import { ApiError } from "../api/client";

interface Props { accountId: number; assetClass?: Account["asset_class"]; }

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e);
}

export function SleeveManagerCard({ accountId, assetClass }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const accountQ = useQuery<Account>({ queryKey: ["accounts", accountId], queryFn: () => accountsApi.get(accountId) });
  const algosQ = useQuery<Algo[]>({ queryKey: ["algos"], queryFn: algosApi.list });
  const linksQ = useQuery<AccountAlgoLink[]>({ queryKey: ["accounts", accountId, "links"], queryFn: () => accountsApi.links(accountId) });
  const sleevePerfQ = useQuery<SleevePerformance[]>({ queryKey: ["portfolio", "sleeves"], queryFn: () => portfolio.sleeves(), refetchInterval: 15_000 });

  const rows = useMemo(() => {
    const algoList = (algosQ.data ?? []).filter(a => {
      const scope = assetClass ?? accountQ.data?.asset_class;
      if (!scope || scope === "multi_asset") return true;
      const mk = a.market.toLowerCase();
      if (scope === "futures") return ["mnq","mes","m2k","mym","mgc","mcl","6j"].includes(mk);
      if (scope === "crypto") return mk.endsWith("usdt");
      if (scope === "options") return mk.startsWith("opt_") || mk.endsWith("_options");
      return !mk.endsWith("usdt") && !["mnq","mes","m2k","mym","mgc","mcl","6j"].includes(mk);
    });
    const links = linksQ.data ?? [];
    const lb = new Map(links.map(l => [l.algo_id, l]));
    const perfMap = new Map((sleevePerfQ.data ?? []).map(p => [p.algo_id, p]));

    return algoList.filter(a => !a.retired).map(a => ({ algo: a, link: lb.get(a.id) ?? null, perf: perfMap.get(a.id) ?? null })).sort((a,b) => a.algo.name.localeCompare(b.algo.name));
  }, [algosQ.data, linksQ.data, sleevePerfQ.data, assetClass, accountQ.data?.asset_class]);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["accounts", accountId, "links"] }); qc.invalidateQueries({ queryKey: ["algos"] }); };
  const [err, setErr] = useState<string | null>(null);
  const patch = useMutation({ mutationFn: (v: { algoId: number; body: any }) => accountsApi.patchLink(accountId, v.algoId, v.body), onSuccess: () => { setErr(null); invalidate(); }, onError: (e) => setErr(errMsg(e)) });
  const link = useMutation({ mutationFn: (algoId: number) => accountsApi.linkAlgo(accountId, { algo_id: algoId, contracts: 1 }), onSuccess: () => { setErr(null); invalidate(); }, onError: (e) => setErr(errMsg(e)) });
  const unlink = useMutation({ mutationFn: (algoId: number) => accountsApi.unlink(accountId, algoId), onSuccess: () => { setErr(null); invalidate(); }, onError: (e) => setErr(errMsg(e)) });

  const busy = patch.isPending || link.isPending || unlink.isPending;

  if (algosQ.isLoading || linksQ.isLoading) return <div className="card"><h2>Sleeve manager</h2><div className="muted small">loading...</div></div>;

  return (
    <div className="card">
      <h2>Sleeve manager</h2>
      <div className="muted small" style={{marginBottom:6,fontSize:10}}>
        Link/unlink sleeves, set contracts, toggle active. Click any row for full insight. Margins: AMP Futures intraday / overnight.
      </div>
      {err && <div className="error-banner">{err}</div>}
      {rows.length === 0 ? <div className="muted small">no sleeves</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Sleeve</th><th>Market</th><th>Status</th><th>Active</th><th className="num">Size</th><th className="num">Avg Trade</th><th className="num">Max DD</th><th/>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <SleeveRow key={r.algo.id} row={r} busy={busy}
                  onActive={(active) => patch.mutate({algoId:r.algo.id, body:{active}})}
                  onContracts={(contracts) => patch.mutate({algoId:r.algo.id, body:{contracts}})}
                  onLink={() => link.mutate(r.algo.id)}
                  onUnlink={() => unlink.mutate(r.algo.id)}
                  onNavigate={() => navigate("/sleeves/" + r.algo.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SleeveRow({ row, busy, onActive, onContracts, onLink, onUnlink, onNavigate }: {
  row: { algo: Algo; link: AccountAlgoLink | null; perf: SleevePerformance | null; };
  busy: boolean;
  onActive: (active: boolean) => void;
  onContracts: (contracts: number) => void;
  onLink: () => void;
  onUnlink: () => void;
  onNavigate: () => void;
}) {
  const { algo, link, perf } = row;
  const status = (algo.status ?? "stopped").toLowerCase();
  const isRunning = status === "running";
  const [draft, setDraft] = useState<string>(link ? String(link.contracts) : "1");
  useEffect(() => { if (link) setDraft(String(link.contracts)); }, [link?.contracts, link]);

  const saveContracts = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 1) { setDraft(link ? String(link.contracts) : "1"); return; }
    if (link && n === link.contracts) return;
    onContracts(n);
  };


  return (
    <tr onClick={onNavigate} style={{cursor:"pointer"}}>
      <td><span className="mono" style={{fontSize:11,fontWeight:600}}>{algo.name}</span></td>
      <td><span className="pill dim" style={{fontSize:8}}>{algo.market?.toUpperCase()}</span></td>
      <td><span className={"pill "+(isRunning?"good":"dim")} style={{fontSize:8}}>{status}</span></td>
      {link ? (
        <>
          <td onClick={e=>e.stopPropagation()}>
            <button type="button" className={link.active?"primary":""} style={{padding:"2px 8px",fontSize:10}} disabled={busy}
              onClick={()=>onActive(!link.active)}>
              {link.active?"on":"off"}
            </button>
          </td>
          <td className="num" onClick={e=>e.stopPropagation()}>
            <input style={{width:48,textAlign:"right",fontSize:10,padding:"1px 4px"}}
              value={draft} disabled={busy} inputMode="numeric"
              onChange={e=>setDraft(e.target.value)} onBlur={saveContracts}
              onKeyDown={e=>{if(e.key==="Enter")(e.target as HTMLInputElement).blur();}}/>
          </td>
          <td className="num mono" style={{fontSize:9}}>{perf && perf.trade_count > 0 ? "$" + (perf.pnl_total / perf.trade_count).toFixed(0) : "-"}</td>
          <td className="num mono" style={{fontSize:9,color:perf && perf.drawdown > 1000 ? "var(--bad)" : "var(--fg-primary)"}}>{perf ? "$" + perf.drawdown.toFixed(0) : "-"}</td>
          <td onClick={e=>e.stopPropagation()}>
            <button type="button" className="btn-xs" disabled={busy} onClick={()=>{if(window.confirm("Unlink?")) onUnlink();}}>Unlink</button>
          </td>
        </>
      ) : (
        <>
          <td className="muted small">-</td>
          <td className="num muted small">-</td>
          <td className="num muted small" style={{fontSize:9}}>{perf && perf.trade_count > 0 ? "$" + (perf.pnl_total / perf.trade_count).toFixed(0) : "-"}</td>
          <td className="num muted small" style={{fontSize:9}}>{perf ? "$" + perf.drawdown.toFixed(0) : "-"}</td>
          <td onClick={e=>e.stopPropagation()}>
            <button type="button" className="btn-xs btn-primary" disabled={busy} onClick={onLink}>+Link</button>
          </td>
        </>
      )}
    </tr>
  );
}
